import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

const CONSENT_COOKIE_NAME = 'lb_cookie_consent_v1';

const writeConsentCookie = (consent: Consent) => {
  try {
    const value = encodeURIComponent(JSON.stringify(consent));
    const maxAge = 60 * 60 * 24 * 365; // 1 year

    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const host = window.location.hostname;
    const isLocalhost = /localhost$/i.test(host);

    let domainPart = '';
    if (!isLocalhost && host.includes('.')) {
      const parts = host.split('.');
      // use registrable domain (last two segments) e.g. lucidbook.com
      const baseDomain = parts.slice(-2).join('.');
      domainPart = `; Domain=.${baseDomain}`;
    }

    document.cookie = `${CONSENT_COOKIE_NAME}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax${domainPart}`;
  } catch (e) {
    console.error('Error writing consent cookie', e);
  }
};

const readConsentCookie = (): Consent | null => {
  try {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${CONSENT_COOKIE_NAME}=([^;]+)`)
    );
    if (!match) return null;
    const raw = decodeURIComponent(match[1]);
    const parsed = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      typeof (parsed as any).necessary === 'boolean' &&
      typeof (parsed as any).analytics === 'boolean' &&
      typeof (parsed as any).marketing === 'boolean'
    ) {
      return parsed as Consent;
    }
    return null;
  } catch (e) {
    console.error('Error reading consent cookie', e);
    return null;
  }
};

type Consent = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

interface CookieContextType {
  consent: Consent;
  setConsent: (consent: Consent) => void;
  resetConsent: () => void;
  isConsentSet: boolean;
}

const defaultConsent: Consent = {
  necessary: true,
  analytics: false,
  marketing: false,
};

const CookieContext = createContext<CookieContextType>({
  consent: defaultConsent,
  setConsent: () => {},
  resetConsent: () => {},
  isConsentSet: false,
});

export const CookieProvider = ({ children }: { children: ReactNode }) => {
  const [consent, setConsentState] = useState<Consent>(defaultConsent);
  const [isConsentSet, setIsConsentSet] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const storedConsent = localStorage.getItem('cookieConsent');
      const storedGiven = localStorage.getItem('cookieConsentGiven');
      console.log('Initializing cookie consent from localStorage / cookie...');

      if (storedConsent && storedGiven === 'true') {
        const parsed = JSON.parse(storedConsent) as Consent;
        setConsentState(parsed);
        setIsConsentSet(true);
        // Ensure cookie is aligned with localStorage
        writeConsentCookie(parsed);
        return;
      }

      // Fallback: try cookie if localStorage is empty
      const cookieConsent = readConsentCookie();
      if (cookieConsent) {
        setConsentState(cookieConsent);
        setIsConsentSet(true);
        // Mirror to localStorage so existing code keeps working
        localStorage.setItem('cookieConsent', JSON.stringify(cookieConsent));
        localStorage.setItem('cookieConsentGiven', 'true');
      }
    } catch (e) {
      console.error('Error initializing cookie consent:', e);
    }
  }, []);

  const setConsent = (newConsent: Consent) => {
    console.log('setConsent called with:', newConsent);
    localStorage.setItem('cookieConsent', JSON.stringify(newConsent));
    localStorage.setItem('cookieConsentGiven', 'true');
    writeConsentCookie(newConsent);
    setConsentState(newConsent);
    setIsConsentSet(true);
  };

  const resetConsent = () => {
    try {
      localStorage.removeItem('cookieConsent');
      localStorage.removeItem('cookieConsentGiven');

      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        // Expire host-only cookie
        document.cookie = `${CONSENT_COOKIE_NAME}=; Path=/; Max-Age=0`;

        // Expire domain cookie (e.g. .lucidbook.com) if applicable
        const host = window.location.hostname;
        const isLocalhost = /localhost$/i.test(host);

        if (!isLocalhost && host.includes('.')) {
          const parts = host.split('.');
          const baseDomain = parts.slice(-2).join('.');
          document.cookie = `${CONSENT_COOKIE_NAME}=; Path=/; Max-Age=0; Domain=.${baseDomain}`;
        }
      }
    } catch (e) {
      console.error('Error resetting cookie consent:', e);
    }

    setConsentState(defaultConsent);
    setIsConsentSet(false);
  };

  return (
    <CookieContext.Provider value={{ consent, setConsent, resetConsent, isConsentSet }}>
      {children}
    </CookieContext.Provider>
  );
};

export const useCookieConsent = () => useContext(CookieContext);